import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
    ProfileApiService,
    MailAccountResponse,
    MailProvider
} from '../services/profile-api.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';

/**
 * Parámetros que venían ANTES del `#` al abrir la app (formato antiguo del retorno OAuth de
 * correo: `/?status=error&...#/account-settings`). Se leen aquí, al cargar el bundle, porque el
 * router de Angular reescribe la URL durante el arranque y para cuando el componente se inicializa
 * `window.location.search` ya está vacío. Solo sirve como red de seguridad mientras el backend
 * corregido no esté desplegado; el formato correcto es `#/account-settings?status=...`.
 */
const PARAMS_PREVIOS_AL_HASH = new URLSearchParams(window.location.search);

/**
 * INC_CRM_013 inc1 — "el botón se queda en Conectando... indefinidamente".
 *
 * La conexión OAuth sale del CRM con `window.location.href`, así que el resultado no vuelve por
 * el `subscribe` sino por una navegación nueva. Si el proveedor rechaza la petición (p. ej.
 * `redirect_uri` no registrado en su consola) o el usuario retrocede sin autorizar, no hay
 * callback y la pantalla no se entera de nada. Se deja constancia del intento en `sessionStorage`
 * —sobrevive a la recarga y al bfcache, y muere con la pestaña— para poder detectar al volver
 * que la vinculación quedó a medias, devolver el botón a su estado original e informar.
 */
const INTENTO_OAUTH_KEY = 'mail_oauth_intento';

/** Pasado este tiempo el intento se considera basura de una sesión anterior y se descarta en silencio. */
const INTENTO_OAUTH_TTL_MS = 30 * 60 * 1000;

interface IntentoOAuth {
    provider: MailProvider;
    startedAt: number;
}

/**
 * INC_CRM_013 inc2/inc5 — "Configuración de cuenta".
 *
 * Todo lo que es seguridad e integraciones de la cuenta: cuentas de correo conectadas,
 * cambio de contraseña y zona de peligro. Los datos de la persona (foto, información
 * personal, zona horaria) están en `ProfileComponent` (`/profile`).
 */
@Component({
    selector: 'app-account-settings',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        PageBreadcrumbComponent,
        ButtonComponent
    ],
    templateUrl: './account-settings.component.html'
})
export class AccountSettingsComponent implements OnInit {
    passwordForm!: FormGroup;
    savingPassword = false;

    mailAccounts: MailAccountResponse[] = [];
    loadingMailAccounts = false;
    connectingProvider: MailProvider | null = null;
    disconnectingProvider: MailProvider | null = null;

    deletingData = false;
    deleteConfirmText = '';

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private profileApi: ProfileApiService,
        private router: Router,
        private route: ActivatedRoute,
        private datetimeService: DatetimeService,
        private toast: ToastService,
        private confirmDialog: ConfirmDialogService
    ) {
        this.initForm();
    }

    ngOnInit(): void {
        this.loadMailAccounts();
        this.handleOAuthReturn();
        this.resolveIntentoOAuthAbandonado();
    }

    /**
     * El navegador puede restaurar esta pantalla desde el bfcache al pulsar "atrás" en la página
     * del proveedor: el componente NO se vuelve a crear, así que `connectingProvider` seguiría
     * mostrando "Conectando..." para siempre. `pageshow` con `persisted` es el único aviso.
     */
    @HostListener('window:pageshow', ['$event'])
    onPageShow(event: PageTransitionEvent): void {
        if (!event.persisted) return;
        this.resolveIntentoOAuthAbandonado();
    }

    private initForm(): void {
        this.passwordForm = this.fb.group({
            currentPassword: ['', [Validators.required, Validators.minLength(6)]],
            newPassword: ['', [Validators.required, Validators.minLength(8)]],
            confirmPassword: ['', Validators.required]
        }, { validators: this.passwordMatchValidator });
    }

    private passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
        const newPassword = group.get('newPassword')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        return newPassword === confirmPassword ? null : { passwordMismatch: true };
    }

    // ── Retorno del OAuth de correo ──

    private handleOAuthReturn(): void {
        this.route.queryParamMap.subscribe(params => {
            // Los parámetros pueden venir dentro del hash (`#/account-settings?status=...`, que es
            // lo que lee el router) o antes de él (`/?status=...#/account-settings`), que es como
            // los dejaba el backend antiguo y el router nunca ve. Se aceptan ambos para que la
            // pantalla informe del resultado aunque el backend no esté aún desplegado.
            const leer = (clave: string) => params.get(clave) ?? PARAMS_PREVIOS_AL_HASH.get(clave);

            const status = leer('status');
            const provider = leer('provider') as MailProvider | null;
            const message = leer('message');
            const email = leer('email');

            if (!status) return;

            // El proveedor sí respondió: el intento ya está resuelto, gane o pierda.
            this.olvidarIntentoOAuth();
            this.connectingProvider = null;

            if (status === 'success') {
                const providerLabel = provider ? this.getProviderLabel(provider) : 'correo';
                this.toast.success({
                    title: 'Cuenta conectada',
                    message: email
                        ? `La cuenta ${email} fue conectada correctamente con ${providerLabel}.`
                        : `La cuenta de ${providerLabel} fue conectada correctamente.`
                });
            }

            if (status === 'error') {
                this.toast.error({
                    title: 'Error de conexión',
                    message: this.describeOAuthError(message, provider)
                });
            }

            this.loadMailAccounts();
            this.clearOAuthParams();
        });
    }

    /**
     * Traduce el código que devuelve el backend a algo accionable. Los códigos del proveedor
     * (`provider_error:...`) llegan en inglés y crudos; el caso de `redirect_uri` es de
     * configuración de la consola de Google/Microsoft y no lo puede resolver el usuario.
     */
    private describeOAuthError(message: string | null, provider: MailProvider | null): string {
        const providerLabel = provider ? this.getProviderLabel(provider) : 'el proveedor';

        if (!message) return 'No se pudo completar la conexión de correo.';

        if (message.includes('redirect_uri')) {
            return `${providerLabel} rechazó la dirección de retorno del CRM (redirect_uri). `
                + 'Es un ajuste de la aplicación en la consola del proveedor: contacta al administrador.';
        }

        const conocidos: Record<string, string> = {
            missing_state: 'La solicitud llegó incompleta. Vuelve a intentar la conexión.',
            invalid_state: 'La solicitud ya no es válida. Vuelve a intentar la conexión.',
            state_expired: 'La solicitud caducó. Vuelve a intentar la conexión.',
            state_already_used: 'Esa autorización ya se usó. Vuelve a intentar la conexión.',
            provider_mismatch: 'La respuesta no corresponde al proveedor solicitado. Vuelve a intentarlo.',
            missing_code: `${providerLabel} no devolvió el código de autorización. Vuelve a intentarlo.`,
            oauth_error: 'No se pudo completar la conexión de correo.'
        };

        if (conocidos[message]) return conocidos[message];

        if (message.startsWith('provider_error:access_denied')) {
            return `Cancelaste el permiso en ${providerLabel}. La cuenta no quedó vinculada.`;
        }

        return message;
    }

    private clearOAuthParams(): void {
        // Consumidos: si no se vacían, volver a esta pantalla en la misma sesión repetiría el toast.
        Array.from(PARAMS_PREVIOS_AL_HASH.keys()).forEach(clave => PARAMS_PREVIOS_AL_HASH.delete(clave));
        if (window.location.search) {
            window.history.replaceState({}, '', window.location.pathname + window.location.hash);
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { status: null, provider: null, email: null, message: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    // ── Cuentas de correo ──

    loadMailAccounts(): void {
        this.loadingMailAccounts = true;

        this.profileApi.getMailAccounts().subscribe({
            next: (accounts) => {
                this.mailAccounts = accounts || [];
                this.loadingMailAccounts = false;
            },
            error: (error) => {
                this.loadingMailAccounts = false;
                this.toast.error({
                    title: 'Error',
                    message: error.error?.mensaje || 'No se pudieron cargar las cuentas conectadas.'
                });
            }
        });
    }

    async onConnectMail(provider: MailProvider): Promise<void> {
        if (this.isProviderConnected(provider)) {
            const ok = await this.confirmDialog.confirm({
                title: 'Cambiar cuenta vinculada',
                message: `Vas a cambiar la cuenta de ${this.getProviderLabel(provider)} ya vinculada. Esta acción solo se permite una vez. ¿Deseas continuar?`,
                confirmText: 'Sí, cambiar cuenta'
            });
            if (!ok) return;

            this.toast.warning({
                title: 'Cambio de cuenta',
                message: `Recuerda: la cuenta de ${this.getProviderLabel(provider)} solo se puede cambiar una vez.`
            });
        }

        this.startOAuthConnection(provider);
    }

    private startOAuthConnection(provider: MailProvider): void {
        this.connectingProvider = provider;

        this.profileApi.getAuthorizeUrl(provider).subscribe({
            next: (response) => {
                const url = response?.url?.trim();
                if (!url) {
                    // Sin URL no hay redirección: sin este guardia se navegaba a ".../undefined"
                    // y el botón se quedaba clavado en "Conectando...".
                    this.connectingProvider = null;
                    this.toast.error({
                        title: 'Error',
                        message: `No se pudo iniciar la conexión con ${this.getProviderLabel(provider)}. `
                            + 'Contacta al administrador.'
                    });
                    return;
                }

                this.recordarIntentoOAuth(provider);
                window.location.href = url;
            },
            error: (error) => {
                this.connectingProvider = null;
                this.olvidarIntentoOAuth();
                this.toast.error({
                    title: 'Error',
                    message: error.error?.mensaje
                        || `No se pudo iniciar la conexión con ${this.getProviderLabel(provider)}.`
                });
            }
        });
    }

    // ── Intento de vinculación a medias (INC_CRM_013 inc1) ──

    private recordarIntentoOAuth(provider: MailProvider): void {
        try {
            const intento: IntentoOAuth = { provider, startedAt: Date.now() };
            sessionStorage.setItem(INTENTO_OAUTH_KEY, JSON.stringify(intento));
        } catch {
            // sessionStorage puede estar bloqueado (modo privado / cookies de terceros): el aviso
            // se pierde, pero la conexión sigue funcionando.
        }
    }

    private olvidarIntentoOAuth(): void {
        try {
            sessionStorage.removeItem(INTENTO_OAUTH_KEY);
        } catch {
            // ídem
        }
    }

    private leerIntentoOAuth(): IntentoOAuth | null {
        try {
            const crudo = sessionStorage.getItem(INTENTO_OAUTH_KEY);
            if (!crudo) return null;

            const intento = JSON.parse(crudo) as IntentoOAuth;
            if (intento?.provider !== 'google' && intento?.provider !== 'microsoft') return null;
            if (!intento.startedAt || Date.now() - intento.startedAt > INTENTO_OAUTH_TTL_MS) return null;

            return intento;
        } catch {
            return null;
        }
    }

    /**
     * Se volvió a esta pantalla con un intento pendiente y sin `status`: el proveedor nunca llamó
     * al callback del CRM. Pasa cuando la consola del proveedor rechaza el `redirect_uri` (el
     * usuario ve el error del proveedor y pulsa "atrás") o cuando cancela la autorización.
     */
    private resolveIntentoOAuthAbandonado(): void {
        const intento = this.leerIntentoOAuth();
        this.olvidarIntentoOAuth();

        if (!intento) {
            // Sin intento pendiente el botón nunca debe quedar bloqueado.
            this.connectingProvider = null;
            return;
        }

        this.connectingProvider = null;
        this.loadMailAccounts();

        this.toast.warning({
            title: 'Vinculación no completada',
            message: `No se completó la vinculación con ${this.getProviderLabel(intento.provider)}. `
                + 'Si el proveedor mostró un error técnico, contacta al administrador del CRM.'
        });
    }

    async onDisconnectMail(provider: MailProvider): Promise<void> {
        const ok = await this.confirmDialog.confirmDanger({
            title: 'Desconectar cuenta',
            message: `¿Seguro que deseas desconectar tu cuenta de ${this.getProviderLabel(provider)}?`,
            confirmText: 'Sí, desconectar'
        });
        if (!ok) return;

        this.disconnectingProvider = provider;
        this.profileApi.disconnectMailAccount(provider).subscribe({
            next: () => {
                this.disconnectingProvider = null;
                this.loadMailAccounts();
                this.toast.success({
                    title: 'Cuenta desconectada',
                    message: `Cuenta de ${this.getProviderLabel(provider)} desconectada correctamente.`
                });
            },
            error: (error) => {
                this.disconnectingProvider = null;
                this.toast.error({
                    title: 'Error',
                    message: error.error?.mensaje || 'No se pudo desconectar la cuenta.'
                });
            }
        });
    }

    getProviderAccount(provider: MailProvider): MailAccountResponse | null {
        return this.mailAccounts.find(account => account.provider === provider) || null;
    }

    getProviderLabel(provider: MailProvider): string {
        return provider === 'google' ? 'Gmail' : 'Microsoft Outlook';
    }

    getAccountStatus(account?: MailAccountResponse | null): string {
        if (!account) return '';

        const raw = (account as any)?.status ?? (account as any)?.connectionStatus ?? '';
        return typeof raw === 'string' ? raw.toUpperCase() : '';
    }

    isProviderConnected(provider: MailProvider): boolean {
        return this.getAccountStatus(this.getProviderAccount(provider)) === 'CONNECTED';
    }

    canReconnect(provider: MailProvider): boolean {
        const account = this.getProviderAccount(provider);
        const status = this.getAccountStatus(account);
        if (status === 'CONNECTED') return false;
        // PENDING con correo = un intento de vinculación que el proveedor nunca devolvió
        // (p. ej. redirect_uri no registrado): también es un "reconectar", no un alta nueva.
        return status === 'ERROR' || status === 'REVOKED' || !!account?.mailAddress;
    }

    /**
     * Desconectar debe seguir disponible aunque el estado no sea CONNECTED: si una
     * reautorización queda a medias (el proveedor rechaza y nunca vuelve al callback), la
     * cuenta se quedaba en PENDING y el botón desaparecía, dejando al usuario sin forma de
     * soltar la cuenta desde el CRM.
     */
    canDisconnect(provider: MailProvider): boolean {
        const account = this.getProviderAccount(provider);
        if (!account?.mailAddress) return false;
        return this.getAccountStatus(account) !== 'REVOKED';
    }

    getStatusLabel(status?: string | null): string {
        switch ((status || '').toUpperCase()) {
            case 'CONNECTED':
                return 'Conectado';
            case 'REVOKED':
                return 'Revocado';
            case 'ERROR':
                return 'Error';
            case 'PENDING':
                return 'Pendiente';
            default:
                return 'No conectado';
        }
    }

    getProviderStatusLabel(provider: MailProvider): string {
        return this.getStatusLabel(this.getAccountStatus(this.getProviderAccount(provider)));
    }

    getStatusClasses(status?: string | null): string {
        switch ((status || '').toUpperCase()) {
            case 'CONNECTED':
                return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
            case 'REVOKED':
                return 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
            case 'ERROR':
                return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
            case 'PENDING':
                return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
        }
    }

    getProviderStatusClasses(provider: MailProvider): string {
        return this.getStatusClasses(this.getAccountStatus(this.getProviderAccount(provider)));
    }

    formatDateTime(value?: string | null): string {
        return this.datetimeService.formatDateTimeNumeric(value);
    }

    isConnecting(provider: MailProvider): boolean {
        return this.connectingProvider === provider;
    }

    isDisconnecting(provider: MailProvider): boolean {
        return this.disconnectingProvider === provider;
    }

    // ── Contraseña ──

    onChangePassword(): void {
        if (this.passwordForm.invalid) {
            this.passwordForm.markAllAsTouched();
            this.toast.warning({
                title: 'Revisa el formulario',
                message: this.passwordMismatch
                    ? 'Las contraseñas no coinciden.'
                    : 'Completa los campos de contraseña correctamente.'
            });
            return;
        }

        this.savingPassword = true;
        this.profileApi.changePassword({
            currentPassword: this.passwordForm.value.currentPassword,
            newPassword: this.passwordForm.value.newPassword
        }).subscribe({
            next: () => {
                this.savingPassword = false;
                this.passwordForm.reset();
                this.toast.success({
                    title: 'Contraseña actualizada',
                    message: 'Tu contraseña se cambió correctamente.'
                });
            },
            error: (error) => {
                this.savingPassword = false;
                this.toast.error({
                    title: 'Error',
                    message: error.error?.message || error.error?.mensaje || 'No se pudo cambiar la contraseña.'
                });
            }
        });
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.passwordForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.passwordForm.get(fieldName);
        if (!field || !field.errors) return '';

        if (field.errors['required']) return 'Este campo es requerido';
        if (field.errors['minlength']) return `Mínimo ${field.errors['minlength'].requiredLength} caracteres`;

        return '';
    }

    get passwordMismatch(): boolean {
        return (this.passwordForm.hasError('passwordMismatch')
            && !!this.passwordForm.get('confirmPassword')?.touched) || false;
    }

    // ── Zona de peligro ──

    async onDeleteMyData(): Promise<void> {
        if (this.deleteConfirmText !== 'ELIMINAR') {
            this.toast.warning({
                title: 'Confirmación pendiente',
                message: 'Escribe ELIMINAR para confirmar la eliminación de tu cuenta.'
            });
            return;
        }

        const ok = await this.confirmDialog.confirmDanger({
            title: 'Eliminar mi cuenta y datos',
            message: 'Esta acción es irreversible. Se revocarán tus conexiones con Google/Microsoft, se eliminarán tus datos personales y se desactivará tu cuenta. ¿Estás seguro?',
            confirmText: 'Eliminar definitivamente'
        });
        if (!ok) return;

        this.deletingData = true;
        this.profileApi.deleteMyData().subscribe({
            next: () => {
                this.deletingData = false;
                this.authService.logout();
                this.router.navigate(['/signin']);
            },
            error: (err) => {
                this.deletingData = false;
                this.toast.error({
                    title: 'Error',
                    message: err.error?.message || 'No se pudieron eliminar los datos. Intenta nuevamente.'
                });
            }
        });
    }
}
